import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import { createIdentitySession, fetchIdentitySession, fetchIdvStatus } from './idv.service';
import { IDV_SESSION_STATUSES, IDV_TRIGGER_TYPES } from './idv.consts';
import { IdvModal } from './IdvModal';
import { fetchEnvWeb as fetchEnvWebAction } from '../../app-main/actions/env';
import { useAsyncLoadingFn } from '@ps-design-system/lib/hooks/use-async-loading-fn';

/**
 * @function
 * @description a React HOC that extends provided Component with IDV functionality (passes idv object as prop to it)
 * @name withIDV
 * @param {object} Component
 * @param {{
 *   // IDV_TRIGGER_TYPES (./idv.consts.js)
 *   triggerType: string/undefined,
 *   cancelUrl: string/undefined,
 * }} options
 * @returns {{
 *     idv: {
 *        initiateIDVSession: function,
 *        isLoading: boolean,
 *        hasValidVerification: boolean,
 *        error: string,
 *        showIDVModal: function
 *     }
 * }}
 */
const withIDV = (Component, {
  triggerType = IDV_TRIGGER_TYPES.bank_account,
  cancelUrl = '/account/methods',
} = {}) => {
  const HOC = (props) => {
    const {
      env,
      SSOid,
      accessToken,
      profileData,
      last4,
      fetchEnvWeb,
      intl,
      profileHasFetched
    } = props;

    // Flag becomes true once initial IDV info loaded
    const [hasLoaded, setHasLoaded] = React.useState(false);

    // Flag becomes true once IDV process starts
    const [isLoading, setIsLoading] = React.useState(false);

    // The value is the current polling timeout (so we can clear it later)
    const [pollingInterval, setPollingInterval] = React.useState(0);

    // Count ticks to stop polling once "n" amount of ticks were passed
    const [, setPollingTicks] = React.useState(0);

    // The value represents if the user has verified fresh session
    const [hasValidVerification, setHasValidVerification] = React.useState(false);

    // Stores last error that could happen during the whole process (initiate, poll, fetch etc.)
    const [error, setError] = React.useState(null);

    // Count ticks to stop polling once "n" amount of ticks were passed
    const [openModal, setOpenModal] = React.useState(false);

    // Configuration object for IdvModal (props that should be assigned dynamically)
    const [modalSettings, setModalSettings] = React.useState({
      closable: false
    });

    // This ref stores callback for IDV check success
    const onSuccessCallbackRef = React.useRef(null);

    // This ref stores callback for IDV check failure
    const onFailureCallbackRef = React.useRef(null);

    // Fetch user's IDV session status
    const [fetchVerificationSessionStatus, isVerificationStatusLoading] = useAsyncLoadingFn(async () => {
      return fetchIdvStatus(SSOid, accessToken, triggerType);

    });

    // Fetch user's IDV session status and update state
    const refreshVerificationSessionStatus = async () => {
      try {
        const sessionStatus = await fetchVerificationSessionStatus();
        setHasValidVerification(sessionStatus?.has_valid_verification);
      } catch (error) {
        setHasValidVerification(false);
      } finally {
        setHasLoaded(true);
      }
    };

    /*
    * Get and save latest verified IDV session
    * Set "hasLoaded" flag to true
    * */
    React.useEffect(() => {
      if (hasLoaded || isVerificationStatusLoading) {
        return;
      }

      if (!SSOid || !accessToken) {
        // Profile finished loading (success or failure) but SSOid is unavailable —
        // can't check IDV, so redirect user to `cancelUrl` rather than hanging forever.
        if (profileHasFetched) {
          window.location.href = cancelUrl;
        }
        return;
      }

      refreshVerificationSessionStatus();
    }, [isVerificationStatusLoading, hasLoaded, SSOid, accessToken, profileHasFetched]);

    // Fetch envWeb if it's missing and not being fetched already
    React.useEffect(() => {
      if (!env?.web?.stripe && !env?.isFetching) {
        fetchEnvWeb();
      }
    }, [env]);

    // Finish polling and cleanup the state (independently if it's a success or failure)
    const endPolling = () => {
      if (pollingInterval) {
        clearTimeout(pollingInterval);
      }
      setIsLoading(false);
      setPollingTicks(0);
      setPollingInterval(0);
    };

    // Save error in the state and end polling
    const handleError = () => {
      setError(intl.formatMessage({
        id: 'idv.generic_error',
        defaultMessage:
          'We were unable to verify your identity. Please try again ensuring you are taking a live image of your document and selfie in a well-lit area, free from glare, and on a dark background.'
      }));
    };

    // Handle polling timeout and end polling
    const handleTimeout = () => {
      endPolling();
      handleError();
      if ('function' === typeof onFailureCallbackRef.current) {
        onFailureCallbackRef.current();
      }
    };

    // Handle IDV failure and end polling
    const handleFailure = () => {
      endPolling();
      handleError();
      if ('function' === typeof onFailureCallbackRef.current) {
        onFailureCallbackRef.current();
      }
    };

    /*
    * Handles IDV success
    * End polling
    * Save verified session as latest succeeded in the state
    * */
    const handleSuccess = (session) => {
      endPolling();
      setHasValidVerification(true);
      setError(null);
      setOpenModal(false);
      if ('function' === typeof onSuccessCallbackRef.current) {
        onSuccessCallbackRef.current(session);
      }
      refreshVerificationSessionStatus();
    };

    /*
    * Handles single poll action (calls GET status endpoint, depends on response it calls handleSuccess, handleFailure or handleTimeout)
    * Increases poll ticks and calls itself until status is verified or failed, or poll ticks exceeded maximum amount (15times / 200ms)
    * */
    const pollOnce = async () => {
      try {
        const session = await fetchIdentitySession(SSOid, accessToken);
        const { verification_status, failure_code, retry } = session || {};

        if (IDV_SESSION_STATUSES.verified === verification_status) {
          handleSuccess(session);
        } else if (IDV_SESSION_STATUSES.failed === verification_status) {
          handleFailure(failure_code, retry);
        } else {
          setPollingTicks((prevTick) =>{
            const nextTick = prevTick + 1;
            if (15 <= nextTick) {
              // 30 seconds
              handleTimeout(failure_code);
            } else {
              setTimeout(pollOnce, 200);
            }
            return nextTick;
          });
        }
      } catch (error) {
        endPolling();
        handleError(error);
      }
    };

    // Starts polling process with delay (2000ms) to let BE process IDV before
    const startPolling = async () => {
      setPollingInterval(
        setTimeout(pollOnce, 2000)
      );
    };

    /**
     * The start or IDV process
     * Fetches new IDV session and calls stipe SDK to display IDV modal
     * If no errors start polling process
     *
     * @param onSuccess - callback when IDV succeeded
     * @param onFailure - callback when IDV failed
     */
    const initiateIDVSession = async ({
      onSuccess = onSuccessCallbackRef.current,
      onFailure = onFailureCallbackRef.current
    } = {}) => {
      if (!env?.web?.stripe.stripe_public_key) {
        return;
      }

      onSuccessCallbackRef.current = onSuccess;
      onFailureCallbackRef.current = onFailure;

      setError(null);
      setIsLoading(true);

      try {
        const sessionPayload = {
          trigger_type: triggerType,
          first_name: profileData?.first_name,
          last_name: profileData?.last_name,
          email: profileData?.email,
          sso_id: SSOid,
          account_ending: last4,
          device_type: window.navigator.userAgent
        };
        const session = await createIdentitySession(
          SSOid,
          accessToken,
          sessionPayload
        );

        try {
          const stripeInstance = Stripe(env?.web?.stripe.stripe_public_key);
          const result = await stripeInstance.verifyIdentity(session?.client_secret);

          if (result?.error) {
            setIsLoading(false);
          } else {
            startPolling();
          }
        } catch (error) {
          handleError(error);
        }
      } catch (error) {
        handleError(error);
      }
    };

    /**
     * Show IDV modal
     * @param onSuccess - callback when IDV succeeded
     * @param onFailure - callback when IDV failed
     * @param onClose - callback when modal is closed
     * @param onBackClick - callback when Back button clicked
     * @param closable - allow/disallow closing modal
     */
    const showIDVModal = ({
      onSuccess = onSuccessCallbackRef.current,
      onFailure = onFailureCallbackRef.current,
      onClose,
      onBackClick,
      closable = false
    } = {}) => {
      onSuccessCallbackRef.current = onSuccess;
      onFailureCallbackRef.current = onFailure;

      setOpenModal(true);
      setModalSettings({
        closable,
        onClose,
        onBackClick
      });
    };

    return <>
      <Component
        {...props}
        idv={{
          initiateIDVSession,
          isLoading,
          hasValidVerification,
          showIDVModal,
          hasLoaded,
          error
        }}
      />
      <IdvModal
        open={openModal}
        onOpenChange={setOpenModal}
        initiateIDVSession={initiateIDVSession}
        isLoading={isLoading}
        error={error}
        {...modalSettings}
      />
    </>;
  };

  HOC.propTypes = {
    env: PropTypes.object,
    SSOid: PropTypes.string,
    accessToken: PropTypes.string,
    profileData: PropTypes.object,
    last4: PropTypes.string,
    fetchEnvWeb: PropTypes.func,
    intl: intlShape,
    profileHasFetched: PropTypes.bool
  };

  const mapStateToProps = (state) => {
    const { env, profile, auth } = state;

    const SSOid = profile?.SSOid;
    const accessToken = auth?.accessToken;

    return {
      env,
      SSOid,
      profileData: profile?.data,
      accessToken,
      profileHasFetched: profile?.hasFetched ?? false
    };
  };

  const mapDispatchToProps = (dispatch) => ({
    fetchEnvWeb: () => dispatch(fetchEnvWebAction())
  });

  return connect(mapStateToProps, mapDispatchToProps)(injectIntl(HOC));
};

export { withIDV };
